import {
  BasicBlockProps,
  BlockId,
  blockPropsMap,
} from '../../../game/defs/block';
import { TabScreenOptions } from '../../../odie/plugins/nav/TabNav';
import { BlockButton, BlockButtonType } from '../../ui/BlockButton';
import { BasicTabScreen } from '../../../lib/ui/BasicTabScreen';
import { ScrollView } from '@play-co/timestep-core/ui';
import { getApplication } from 'src/lib/utils';
import { CountSizePopup } from './CountSizePopup';
import {
  CountColor,
  CountSize,
  blockOptionFromCountColor,
  blockOptionFromCountSize,
} from '../../../game/util/blockOption';
import { EditorScreen } from '../../EditorScreen';
import { ChameleonColor } from '../../../game/views/blocks/ChameleonBlockView';
import { MapDef } from '../../../game/defs/map';
import { CountColorPopup } from './CountColorPopup';

/*
    puzzle editor block menu
*/
export class EditorBlockMenu extends BasicTabScreen {
  // fields
  //-------------------------------------------------------------------------
  // state
  private _selectedButton?: BlockButton;
  private _scroller!: ScrollView;
  private _buttons: BlockButton[] = [];
  private _keyboardShortcuts = [
    'f1',
    'f2',
    'f3',
    'f4',
    'f5',
    'f6',
    'f7',
    'f8',
    'f9',
    'f10',
    'f11',
    'f12',
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    '0',
    'q',
    'w',
    'e',
    'r',
    't',
    'y',
    'u',
    'i',
    'o',
    'p',
    'a',
    's',
    'd',
    'f',
    'g',
    'h',
    'j',
    'k',
    'l',
    'z',
    'x',
    'c',
    'v',
    'b',
    'n',
    'm',
  ];

  // properties
  //-------------------------------------------------------------------------
  public static assets(): string[] {
    return BlockButton.assets()!;
  }

  public get selectedButton(): BlockButton | undefined {
    return this._selectedButton;
  }

  // init
  //-------------------------------------------------------------------------
  constructor(options: TabScreenOptions) {
    super(options);

    // spawn scrroller
    this._spawnScroller();

    // spawn buttons
    this._spawnButtons();

    // Add keyboard shortcuts.
    document.addEventListener('keyup', (key) => {
      const index = this._keyboardShortcuts.indexOf(key.key.toLowerCase());
      if (index >= 0) {
        this._onButtonPress(this._buttons[index]);
      }
    });

    // Add mouse wheel handler. (conflicts with the scroller)
    // document.addEventListener('wheel', (event) => {
    //   const delta = Math.sign(event.deltaY);
    //   const currentIndex = this._selectedButton
    //     ? this._buttons.indexOf(this._selectedButton)
    //     : 0;

    //   const newIndex = mod(currentIndex + delta, this._buttons.length);
    //   this._onButtonPress(this._buttons[newIndex]);
    // });
  }

  // impl
  //-------------------------------------------------------------------------
  public async prepare() {
    //
  }

  public hidden() {
    //
  }

  public async updateDynamicOptions(screen: EditorScreen) {
    if (!screen) return;

    this._buttons.forEach((button) => {
      switch (button.def.id) {
        case 'chameleon':
          this.updateChameleonOptions(button, screen.getMapDef());
          break;
      }
    });
  }

  // private: events
  //-------------------------------------------------------------------------
  private async _onButtonPress(button: BlockButton) {
    // if pressing already selected
    if (button === this._selectedButton) {
      // show options menu
      // await this._showOptionsMenu(button);
      this._switchBlockDef(button);
      // button.nextOption();
    } else {
      // select new button
      this._selectButton(button);
    }
  }

  // private: spawn
  //-------------------------------------------------------------------------
  private _spawnScroller() {
    // spawn scroller
    this._scroller = new ScrollView({
      superview: this.view.view,
      width: this.width,
      height: this.height,
      backgroundColor: 'black',
      scrollX: false,
      scrollY: true,
    });
  }

  private _spawnButtons() {
    let x = 0;
    let y = 0;
    const space = 78;

    // spawn clear button and make selected
    const buttonClear = this._spawnButton(x, y, 'clear');
    this._buttons.push(buttonClear);
    this._selectButton(buttonClear);
    x += space;

    // spawn tile button
    const buttonTile = this._spawnButton(x, y, 'tile');
    this._buttons.push(buttonTile);
    x += space;

    const keys = Object.keys(blockPropsMap);

    // spawn remaining block buttons
    keys
      .filter((key: string) => !blockPropsMap[key as BlockId].noEditor)
      .forEach((blockId) => {
        // spawn block button
        const button = this._spawnButton(x, y, 'block', blockId as BlockId);
        this._buttons.push(button);

        // iterate position
        x += space;
        if (x >= this.width - space) {
          x = 0;
          y += space;
        }
      });
  }

  private _spawnButton(
    x: number,
    y: number,
    type: BlockButtonType,
    blockId?: BlockId,
  ): BlockButton {
    // create button
    const button = new BlockButton({
      type,
      def: { id: blockId! },
      key: this._keyboardShortcuts[this._buttons.length],
    });

    button.x = x;
    button.y = y;

    // register events
    button.onTap = () => this._onButtonPress(button);
    button.initListeners();

    // spawn
    // this._scroller.content.addChild(button);
    this._scroller.addSubview(button.view);
    this._scroller.setScrollBounds({
      minY: 0,
      maxY: button.y + button.height + 180,
    });

    return button;
  }

  // private: block options - quick in-place switch
  //-------------------------------------------------------------------------
  private _switchBlockDef(button: BlockButton) {
    const props = blockPropsMap[button.def.id];

    // SKip if button has no props (like clear, random spawn etc)
    if (!props) return;

    // Skip def switch if the block should not have editor options
    if (props.noEditorOptions) return;

    const options = props?.options ?? [];

    if (props.optionType === 'countSize') {
      this.openSizeCountPopup(button);
      return;
    }

    if (props.optionType === 'countColor') {
      this.openCountColorPopup(button);
      return;
    }

    // Skip if there are no options to switch.
    if (!options.length) return;

    button.nextOption();
  }

  // private: support
  //-------------------------------------------------------------------------
  private _selectButton(button: BlockButton) {
    // unselect last selected
    if (this._selectedButton) {
      this._selectedButton.selected = false;
    }

    // set new selected
    this._selectedButton = button;
    button.selected = true;
  }

  private openSizeCountPopup(button: BlockButton) {
    const props = blockPropsMap[button.def.id];
    if (props?.optionType !== 'countSize') return;
    const currentOption =
      button.def.option ?? blockOptionFromCountSize(props as CountSize);
    const superview = getApplication().getRootView();
    const popup = new CountSizePopup();
    popup.view.view.updateOpts({ superview });
    popup.prepare({
      ...props,
      option: currentOption,
      onOk: (option: number) => {
        button.def = {
          ...button.def,
          option,
        };
      },
    });
    popup.show();
  }

  private openCountColorPopup(button: BlockButton) {
    const props = blockPropsMap[button.def.id];
    if (props?.optionType !== 'countColor') return;
    const currentOption =
      button.def.option ?? blockOptionFromCountColor(props as CountColor);
    const superview = getApplication().getRootView();
    const popup = new CountColorPopup();
    popup.view.view.updateOpts({ superview });
    popup.prepare({
      ...props,
      option: currentOption,
      onOk: (option: number) => {
        button.def = {
          ...button.def,
          option,
        };
      },
      colors: ['red', 'green', 'blue', 'yellow', 'purple'],
    });
    popup.show();
  }

  private updateChameleonOptions(button: BlockButton, def: MapDef) {
    // grab basic block spawns and map the only available colors to the options
    const override = def.spawns
      .filter((id) => id.includes('basic'))
      .map((id) => {
        const props = blockPropsMap[id] as BasicBlockProps;
        return { color: props.color as ChameleonColor };
      });

    button.setOptionsOverride(override);
  }
}
